import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Box,
  Container,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { CommandBlock } from '../components'
import {
  backIconSx,
  backLinkSx,
  containerSx,
  dividerSx,
  inlineLinkSx,
  listStyle,
  sectionSx,
  sectionTitleSx,
  titleSx,
} from '../styles'

export const DaemonSetupPage: FC = () => {
  return (
    <Container maxWidth="md" sx={containerSx}>
      <Link component={RouterLink} to="/docs" sx={backLinkSx}>
        <ArrowBackIcon sx={backIconSx} />
        Back to Docs
      </Link>

      <Typography variant="h5" sx={titleSx}>
        Daemon Setup Guide
      </Typography>

      <Typography variant="h6" id="what-is-the-daemon" sx={sectionTitleSx}>
        What is the Daemon?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        Background service running on your machine.{' '}
        <Link component={RouterLink} to="/docs/faq#how-it-works" sx={inlineLinkSx}>
          Captures Claude Code session events
        </Link>{' '}
        via Claude Code hooks,{' '}
        <Link component={RouterLink} to="/docs/security" sx={inlineLinkSx}>
          encrypts
        </Link>{' '}
        and streams them to the dashboard through the backend. Also receives encrypted commands from
        the dashboard, decrypts them, and executes them in Claude Code sessions.
      </Typography>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        Prerequisites
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <strong>tmux</strong> — the daemon manages Claude Code sessions through{' '}
        <Link component={RouterLink} to="/docs/faq#why-tmux" sx={inlineLinkSx}>
          tmux
        </Link>
      </Typography>
      <CommandBlock code="brew install tmux" />

      <Typography variant="body1" color="text.secondary" component="div" sx={prerequisiteItemSx}>
        <strong>Claude Code</strong> — must be installed and in PATH
      </Typography>
      <CommandBlock code="curl -fsSL https://claude.ai/install.sh | bash" />

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        Install the Daemon
      </Typography>
      <CommandBlock code="brew install Cordona/tap/c4-daemon" />
      <Typography variant="body2" color="text.secondary" sx={orSeparatorSx}>
        or
      </Typography>
      <CommandBlock code={SHELL_INSTALLER_COMMAND} />

      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        After installation, verify the daemon is installed and on your PATH
      </Typography>
      <CommandBlock code="c4-daemon --version" />
      <Box sx={calloutSx}>
        <InfoOutlinedIcon sx={calloutIconSx} />
        <Typography variant="body2" color="text.secondary">
          If the command is not found, restart your terminal or reload your shell profile
        </Typography>
      </Box>
      <CommandBlock code="source ~/.zshrc" />
      <Typography variant="body2" color="text.secondary" sx={orSeparatorSx}>
        or
      </Typography>
      <CommandBlock code="source ~/.bashrc" />

      <Divider sx={dividerSx} />

      <Typography variant="h6" id="setup-the-daemon" sx={sectionTitleSx}>
        Setup the Daemon
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        Run the daemon for the first time. If no configuration exists, it starts the setup wizard
        automatically.
      </Typography>
      <CommandBlock code="c4-daemon" />
      <Box sx={calloutSx}>
        <InfoOutlinedIcon sx={calloutIconSx} />
        <Typography variant="body2" color="text.secondary">
          Steps 1–3 require artifacts from the dashboard.{' '}
          <Link component={RouterLink} to="/docs/onboarding" sx={inlineLinkSx}>
            See the onboarding guide
          </Link>{' '}
          for how to generate them before running setup.
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={stepListLabelSx}>
        The wizard walks through these steps:
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ol style={listStyle}>
          <li>
            <strong>API Key</strong> — paste the API key generated in the dashboard.
          </li>
          <li>
            <strong>Key Ring</strong> — provide the absolute path to the key ring file exported from
            the dashboard.
          </li>
          <li>
            <strong>Host Identity</strong> — enter the host name and host ID from the dashboard.
          </li>
          <li>
            <strong>Backend Connection</strong> — auto-detected from the API key prefix and
            validated. For live keys (<code>c4-live-*</code>), the production backend is used
            automatically. For dev or test keys, you can configure a local backend endpoint.
          </li>
          <li>
            <strong>Features</strong> — configure daemon behavior (see below).
          </li>
        </ol>
      </Typography>
      <Box sx={warningCalloutSx}>
        <WarningAmberOutlinedIcon sx={warningCalloutIconSx} />
        <Typography variant="body2" color="text.secondary">
          After setup completes, the daemon auto-configures Claude Code hooks to send session
          events. Your existing global Claude Code settings file (
          <code>~/.claude/settings.json</code>) is backed up with a timestamp. Previously configured
          hook commands are preserved alongside the new daemon entries.
        </Typography>
      </Box>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        Features
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        Step 5 of setup configures three independent features:
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>
            <strong>Hooks Processing</strong> — captures Claude Code lifecycle events (session
            start/end, tool use, prompts, notifications) and streams them to the backend. Disable if
            you do not want to send any events from this host, including activity events.
          </li>
          <li>
            <strong>Activity Tracking</strong> — provides the activity feed in the dashboard.
            Disable if you only need conversation events (not recommended).
          </li>
          <li>
            <strong>Event Persistence</strong> — stores events in the backend database for history,
            search, and analytics. When disabled, events are streamed in real-time but not persisted
            — a dashboard refresh or close loses them.
          </li>
        </ul>
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        All three are enabled by default.
      </Typography>

      <Box sx={warningCalloutSx}>
        <WarningAmberOutlinedIcon sx={warningCalloutIconSx} />
        <Typography variant="body2" color="text.secondary">
          If your employer or client has strict policies about storing company data on external
          servers, consider disabling event persistence for transient-only events. All persisted
          data is fully{' '}
          <Link component={RouterLink} to="/docs/security" sx={inlineLinkSx}>
            end-to-end encrypted
          </Link>{' '}
          and completely unreadable by us — only your machines and dashboard can decrypt it. Follow
          your organization&apos;s security guidelines regarding external data storage.
        </Typography>
      </Box>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        Running
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        After initial setup, the daemon starts automatically. See the{' '}
        <Link component={RouterLink} to="/docs/daemon-usage" sx={inlineLinkSx}>
          Using the Daemon
        </Link>{' '}
        page for interactive mode commands to manage sessions, features, and more.
      </Typography>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        Platforms
      </Typography>
      <Table size="small" sx={tableSx}>
        <TableHead>
          <TableRow>
            <TableCell sx={tableHeaderCellSx}>Platform</TableCell>
            <TableCell sx={tableHeaderCellSx}>Architecture</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {PLATFORMS.map(({ platform, architecture }) => (
            <TableRow key={`${platform}-${architecture}`}>
              <TableCell sx={tableLabelCellSx}>{platform}</TableCell>
              <TableCell sx={tableValueCellSx}>{architecture}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Container>
  )
}

const SHELL_INSTALLER_COMMAND = String.raw`curl --proto '=https' --tlsv1.2 -LsSf \
  https://github.com/Cordona/claude-code-agent-releases/releases/latest/download/c4-daemon-installer.sh | sh`

const PLATFORMS: readonly { platform: string; architecture: string }[] = [
  { platform: 'macOS', architecture: 'Apple Silicon' },
  { platform: 'macOS', architecture: 'Intel' },
  { platform: 'Linux', architecture: 'x86_64' },
]

const prerequisiteItemSx: SxProps<Theme> = {
  mt: 2,
  mb: 1,
  fontSize: '0.875rem',
  lineHeight: 1.8,
}

const stepListLabelSx: SxProps<Theme> = {
  mt: 2,
  mb: 1,
  fontSize: '0.875rem',
  lineHeight: 1.8,
}

const orSeparatorSx: SxProps<Theme> = {
  textAlign: 'center',
  my: 1,
  fontSize: '0.875rem',
  fontStyle: 'italic',
}

const calloutSx: SxProps<Theme> = {
  display: 'flex',
  gap: 1.5,
  mt: 2,
  p: 2,
  fontSize: '0.875rem',
  borderLeft: '3px solid',
  borderColor: 'info.main',
  backgroundColor: 'action.hover',
  borderRadius: '0 4px 4px 0',
}

const calloutIconSx: SxProps<Theme> = {
  fontSize: 20,
  color: 'info.main',
  mt: 0.25,
  flexShrink: 0,
}

const warningCalloutSx: SxProps<Theme> = {
  display: 'flex',
  gap: 1.5,
  mt: 2,
  p: 2,
  fontSize: '0.875rem',
  borderLeft: '3px solid',
  borderColor: 'warning.main',
  backgroundColor: 'action.hover',
  '& code': {
    fontFamily: 'monospace',
    fontSize: '1.05em',
    color: (theme) =>
      theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
  },
  borderRadius: '0 4px 4px 0',
}

const warningCalloutIconSx: SxProps<Theme> = {
  fontSize: 20,
  color: 'warning.main',
  mt: 0.25,
  flexShrink: 0,
}

const tableSx: SxProps<Theme> = {
  mt: 2,
  mb: 1,
  '& .MuiTableCell-root': {
    borderBottom: 'none',
    py: 0.75,
    px: 1,
  },
}

const tableHeaderCellSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontWeight: 600,
  borderBottom: '1px solid',
  borderBottomColor: 'divider',
}

const tableLabelCellSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontWeight: 500,
  width: 120,
  whiteSpace: 'nowrap',
}

const tableValueCellSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
}
